import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Box, Paper, Typography, Button, IconButton, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, ListItemIcon, ListItemText, Divider, Menu, MenuItem } from '@mui/material';
import { Undo as UndoIcon, Redo as RedoIcon, History as HistoryIcon, ContentCopy as DuplicateIcon, MoreVert as MoreIcon, PhoneIphone as PhonePreviewIcon, Send as SendIcon } from '@mui/icons-material';
import ValidationSummaryPanel from './ValidationSummaryPanel';
import { problemsFromValidationMap } from './ai/fixRequest';
import ComponentPalette from './ComponentPalette';
import Canvas from './Canvas';
import PropertiesPanel from './PropertiesPanel';
import SaveFlowDialog from './SaveFlowDialog';
import EditableScreenTab from './EditableScreenTab';
import WhatsAppPreviewDialog from './WhatsAppPreviewDialog';
import SendToPhoneDialog from './SendToPhoneDialog';
import FlowConnectionsDrawer from '../flows/FlowConnectionsDrawer';
import HubIcon from '@mui/icons-material/HubOutlined';
import RealtimeFlowPreview from './RealtimeFlowPreview';
import DynamicFlowChip from '../flows/DynamicFlowChip';
import SaveStatusControl from './SaveStatusControl';
import { registerDiagnostics } from '../../utils/diagnostics';
import VersionHistoryDrawer from './VersionHistoryDrawer';
import ErrorBoundary from '../common/ErrorBoundary';
import ConnectWabaDialog from '../environment/dialogs/ConnectWabaDialog';
import { useFlow } from '../../contexts/FlowContext';
import { useWhatsAppConnection } from '../../contexts/WhatsAppConnectionContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useLiveDataConnected } from '../../hooks/useLiveDataConnected';
import FlowTypeControl from './FlowTypeControl';
import ValidationStatus from './ValidationStatus';
import { useFlowBuilder } from '../../hooks/useFlowBuilder';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useFlowValidation } from '../../hooks/useFlowValidation';
import { useFlowSyncStatus } from '../../hooks/useFlowSyncStatus';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useAutoSavePreference } from '../../hooks/useAutoSavePreference';
import { useDirtyState } from '../../hooks/useDirtyState';
import { useDriftCheck } from '../../hooks/useDriftCheck';
import { track } from '../../utils/analytics';
import './FlowBuilder.css';

const FlowBuilder = ({ onFlowSaved, initialScreenId, onActiveScreenChange, onScreensChange, onFixWithAssistant, active = true }) => {
  const { createFlow, currentFlow, setCurrentFlow, updateFlow, getFlow, testEndpoint, reportValidationFeedback } = useFlow();

  // Bumped to force the builder to reload the flow JSON (e.g. after restoring a past version),
  // without normal saves triggering a reload (which would wipe undo history).
  const [reloadNonce, setReloadNonce] = useState(0);

  // Whether this flow's live data has somewhere to go: a booking flow (FlowGate answers it) or a
  // server connected in Connections. Validation asks; re-read when Connections closes.
  const { connected: liveDataConnected, refresh: refreshLiveData } = useLiveDataConnected(currentFlow?.id);


  // Flow builder state and operations
  var {
    screens,
    activeScreen,
    selectedComponent,
    currentScreen,
    flowType,
    setFlowType,
    loadGeneration,
    addScreen,
    removeScreen,
    renameScreen,
    reorderScreens,
    duplicateScreen,
    setActiveScreen,
    addComponent,
    updateComponentProperties,
    deleteComponent,
    duplicateComponent,
    copyComponent,
    pasteComponent,
    reorderComponents,
    addComponentToBranch,
    removeComponentFromBranch,
    reorderBranchComponents,
    setSelectedComponent,
    validateFlow,
    validationMap,
    generateFlowJson,
    updateScreenRouting,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useFlowBuilder(currentFlow, reloadNonce, liveDataConnected);

  // Report the live canvas upward so the AI assistant can be handed the flow as it IS, not as it
  // was last saved. Without this a chat message after an unsaved edit would work from stale JSON
  // and undo the edit on the next turn.
  useEffect(() => {
    if (onScreensChange) onScreensChange(screens);
  }, [screens, onScreensChange]);

  // Keyboard shortcuts: Cmd/Ctrl+Z to undo, Shift+Cmd/Ctrl+Z (or Ctrl+Y) to redo. Ignore when a
  // text field is focused so native text undo still works while editing component properties.
  useEffect(() => {
    const onKeyDown = (e) => {
      const key = (e.key || '').toLowerCase();
      const isUndoRedo = (e.metaKey || e.ctrlKey) && (key === 'z' || key === 'y');
      if (!isUndoRedo) return;
      // Let native undo win inside any editable / selection control so we don't hijack text editing.
      const editable = e.target.closest?.(
        'input, textarea, [contenteditable="true"], [role="textbox"], [role="combobox"], [role="listbox"]'
      );
      if (editable || e.target.isContentEditable) return;
      e.preventDefault();
      if (key === 'y' || e.shiftKey) {
        redo();
      } else {
        undo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  // Deep-linking: apply the ?screen= target exactly once per flow (keyed by flow id, NOT the
  // screen, so the URL writes below can't re-trigger it), then reflect the active screen back into
  // the URL so refresh / link-sharing preserves screen context.
  const appliedForFlowRef = useRef(null);
  useEffect(() => {
    const flowKey = currentFlow?.id ?? 'new';
    if (appliedForFlowRef.current === flowKey) return;
    if (!initialScreenId) {
      if (screens.length) appliedForFlowRef.current = flowKey;
      return;
    }
    if (screens.some(s => s.id === initialScreenId)) {
      appliedForFlowRef.current = flowKey;
      setActiveScreen(initialScreenId);
    }
  }, [initialScreenId, screens, currentFlow?.id, setActiveScreen]);

  useEffect(() => {
    if (onActiveScreenChange) onActiveScreenChange(activeScreen);
  }, [activeScreen, onActiveScreenChange]);

  // Component shortcuts: ⌘/Ctrl+D duplicates the selected component, Delete/Backspace removes it.
  // Skipped while editing a field so text editing is unaffected.
  useEffect(() => {
    const onKeyDown = (e) => {
      const editable = e.target.closest?.(
        'input, textarea, [contenteditable="true"], [role="textbox"], [role="combobox"], [role="listbox"]'
      );
      if (editable) return;
      const key = (e.key || '').toLowerCase();
      const mod = e.metaKey || e.ctrlKey;

      // Paste works without a selection (drops into the active screen).
      if (mod && key === 'v') {
        e.preventDefault();
        pasteComponent();
        return;
      }
      if (!selectedComponent) return;

      if (mod && key === 'c') {
        // Don't hijack a real text copy.
        if (window.getSelection && window.getSelection().toString()) return;
        e.preventDefault();
        copyComponent(selectedComponent.id);
      } else if (mod && key === 'd') {
        e.preventDefault();
        duplicateComponent(selectedComponent.id);
      } else if ((key === 'delete' || key === 'backspace') && selectedComponent.type !== 'Footer') {
        e.preventDefault();
        deleteComponent(selectedComponent.id);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedComponent, duplicateComponent, deleteComponent, copyComponent, pasteComponent]);
  
  // Validation feedback (must come before drag-and-drop so showSnackbar is available)
  const {
    snackbar,
    showSnackbar,
    hideSnackbar,
  } = useFlowValidation(validateFlow, generateFlowJson);

  // Drag and drop operations
  const {
    activeId,
    hoveredComponent,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    customCollisionDetection,
  } = useDragAndDrop(screens, activeScreen, addComponent, reorderComponents, showSnackbar, addComponentToBranch, reorderBranchComponents);
  
  // Saving requires a WABA connection on the active environment (the backend
  // registers/syncs every flow on the WABA). Without one, Save is disabled and
  // a banner offers to start the connection right here.
  const { isConnected } = useWhatsAppConnection();
  const { selectedEnvironment } = useEnvironment();
  const [connectWabaOpen, setConnectWabaOpen] = useState(false);

  // Dialog states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendToPhoneOpen, setSendToPhoneOpen] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);

  const [validationPanelOpen, setValidationPanelOpen] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);

  // Background sync + validation status of the most recently saved flow (ADR-0006).
  const { status: syncStatus, track: trackSync } = useFlowSyncStatus(2000, { active });

  // Background drift check on open: compare our local JSON with WhatsApp's. Until it resolves (or the
  // user reconciles a detected difference) saving is suppressed so we never clobber a remote edit.
  const { driftResolved, drift: flowDrift, resolve: resolveDrift } = useDriftCheck(currentFlow);
  const [reconciling, setReconciling] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useAutoSavePreference();
  // Tracks unsaved local edits, keyed identically to the auto-save baseline below so the two stay in
  // lockstep. markClean() is called after each successful save (auto or manual).
  const { dirty, markClean } = useDirtyState(`${currentFlow?.id ?? 'new'}:${loadGeneration}`, screens);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  const closeMoreMenu = () => setMoreAnchorEl(null);

  // Lightweight summary for SaveFlowDialog, computed sync from builder state,
  // no JSON generation. The actual flow JSON sent on save is regenerated by
  // FlowBuilder.handleSaveFlow via the canonical generateFlowJson().
  const flowSummary = useMemo(() => ({
    screenCount: screens.length,
    componentCount: screens.reduce((sum, s) => sum + (s.components?.length || 0), 0),
    version: '7.3',
  }), [screens]);

  // Publish the live builder state so feedback reports from this page capture the flow the
  // user is actually editing (incl. unsaved edits), the most important state on the page.
  // A ref kept fresh each render means the registered provider always returns the latest.
  const diagRef = useRef({});
  diagRef.current = {
    flowId: currentFlow?.id ?? null,
    flowName: currentFlow?.name ?? null,
    whatsappFlowId: currentFlow?.whatsappFlowId ?? null,
    status: currentFlow?.status ?? null,
    flowType,
    unsavedEdits: dirty,
    activeScreenId: activeScreen?.id ?? null,
    screenCount: flowSummary.screenCount,
    componentCount: flowSummary.componentCount,
    screens, // the live working flow (builder model, incl. unsaved/invalid state)
  };
  useEffect(() => registerDiagnostics('flow', () => diagRef.current), []);

  const handleJumpToComponent = (screenId, componentId) => {
    setActiveScreen(screenId);
    const targetScreen = screens.find((s) => s.id === screenId);
    const targetComponent = targetScreen?.components.find((c) => c.id === componentId);
    if (targetComponent) {
      setSelectedComponent(targetComponent);
    }
    setValidationPanelOpen(false);
  };

  const handleJumpToScreen = (screenId) => {
    setActiveScreen(screenId);
    setSelectedComponent(null);
    setValidationPanelOpen(false);
  };

  var sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Separate sensors for screen tab reordering with activation distance to prevent accidental drags
  var screenTabSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  var handleScreenDragEnd = (event) => {
    var { active, over } = event;
    if (!over || active.id === over.id) return;

    var oldIndex = screens.findIndex(s => s.id === active.id);
    var newIndex = screens.findIndex(s => s.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      reorderScreens(oldIndex, newIndex);
    }
  };

  // Event handlers for dialogs and actions
  const handleSaveFlow = async (flowData) => {
    // Don't persist while the WhatsApp drift check is unresolved, it could overwrite a remote edit.
    if (!driftResolved) {
      showSnackbar('Checking for changes on WhatsApp. Please resolve that first', 'warning');
      return null;
    }
    // Check if flow exists in context (not just currentFlow)
    const existingFlow = currentFlow ? getFlow(currentFlow.id) : null;
    const isUpdate = !!existingFlow;

    const preValidation = validateFlow();
    if (preValidation.errors.length > 0) {
      showSnackbar(`Cannot save: ${preValidation.errors[0]}`, 'error');
      return null;
    }

    // generateFlowJson produces the complete JSON including _builderMeta (conditions, routing)
    const flowJson = await generateFlowJson();

    let savedFlow;

    if (isUpdate) {
      // Update existing flow in backend. snapshot:true records a version-history entry, this is an
      // explicit manual save, unlike the debounced auto-save which must not snapshot.
      savedFlow = await updateFlow(existingFlow.id, {
        name: flowData.name,
        description: flowData.description,
        flowJson,
        flowType,
        endpointUri: flowData.endpointUri,
        categories: flowData.categories,
        snapshot: true,
      });
      setCurrentFlow(savedFlow);
    } else {
      // Create new flow in backend
      savedFlow = await createFlow({
        name: flowData.name,
        description: flowData.description,
        flowJson,
        flowType,
        endpointUri: flowData.endpointUri,
        categories: flowData.categories,
      });
      setCurrentFlow(savedFlow);
    }

    track('flow_saved', {
      isUpdate,
      screenCount: screens.length,
      componentCount: screens.reduce((n, s) => n + (s.components?.length || 0), 0),
    });

    // Notify parent about the saved flow (for URL updates)
    if (onFlowSaved) {
      onFlowSaved(savedFlow);
    }

    markClean();

    // Saving is local-first (ADR-0006): the flow is persisted instantly and synced to Meta in
    // the background. The effect below tracks its sync + validation status from currentFlow and
    // surfaces Meta's verdict via the toolbar status chip.
    return savedFlow;
  };

  // Reflect the saved/loaded flow's background sync + validation status in the toolbar, and keep
  // polling while it is still syncing. Re-runs on save (updatedAt changes) and on flow load.
  useEffect(() => {
    trackSync(currentFlow);
    // Intentionally key on id + updatedAt (not the whole currentFlow object) so unrelated identity
    // changes don't restart polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFlow?.id, currentFlow?.updatedAt, trackSync]);

  // Auto-save existing flows after edits settle. Saving is local-first and instant; the status
  // chip reflects the resulting background sync. New (unsaved) flows are excluded, they need a
  // name first via the Save dialog.
  // Deliberately does not catch: useAutoSave owns the outcome, exposing failures as `autoSaveError`
  // so the toolbar can show them. Swallowing here is what let the safety net fail silently.
  const handleAutoSave = async () => {
    if (!currentFlow?.id) return;
    const flowJson = await generateFlowJson();
    const saved = await updateFlow(currentFlow.id, {
      name: currentFlow.name,
      description: currentFlow.description,
      flowJson,
    });
    setCurrentFlow(saved);
    markClean();
  };

  // Save-now from the status widget: persist the current edits immediately rather than waiting for the
  // debounced auto-save. Unlike auto-save, this is an explicit save and records a version-history
  // snapshot. A flow without an id has no name yet, so route those through the Save dialog instead.
  const handleSaveNow = async () => {
    if (!isConnected) {
      showSnackbar('Connect a WhatsApp Business Account to save flows', 'warning');
      return;
    }
    if (!currentFlow?.id) {
      setSaveDialogOpen(true);
      return;
    }
    if (!driftResolved) {
      showSnackbar('Checking for changes on WhatsApp. Please resolve that first', 'warning');
      return;
    }
    try {
      const flowJson = await generateFlowJson();
      const saved = await updateFlow(currentFlow.id, {
        name: currentFlow.name,
        description: currentFlow.description,
        flowJson,
        snapshot: true,
      });
      setCurrentFlow(saved);
      markClean();
      clearAutoSaveError();
      track('flow_saved', { isUpdate: true, manual: true, screenCount: screens.length });
    } catch (err) {
      showSnackbar(err.message || 'Save failed', 'error');
    }
  };
  // Key on loadGeneration too: when a flow loads, its screens repopulate one render after the id is
  // known. Without this the populate looks like an edit and auto-saves on open. Bumping the key on
  // each load re-baselines the dirty check so only real edits (after the load) trigger a save.
  const { error: autoSaveError, clearError: clearAutoSaveError } = useAutoSave(`${currentFlow?.id ?? 'new'}:${loadGeneration}`, screens, handleAutoSave, {
    // Hold auto-save until the WhatsApp drift check resolves, so edits can't overwrite a remote change
    // before the user has decided how to reconcile. Also respect the user's auto-save preference,
    // with it off, edits stay "Unsaved changes" until an explicit Save now. A missing WABA connection
    // suspends auto-save too (saves sync to the WABA; without one they'd just fail in the background).
    // `active` is false while the assistant pane is what's on screen: it stays mounted rather than
    // unmounted (so its conversation survives the switch), which means it is also watching this same
    // flow, without this, both auto-save loops would race writes against each other.
    enabled: active && !!currentFlow?.id && driftResolved && autoSaveEnabled && isConnected,
    // One toast when auto-save starts failing; the toolbar chip carries it from then on.
    onError: (message) => showSnackbar(`Auto-save failed: ${message}`, 'error'),
  });

  // Flow type is the flow's own, saved with it, so the assistant and the canvas show one answer.
  // A change here is saved at once (it is local metadata, never sent to Meta, so it costs no
  // re-sync), and a change made in the assistant is followed here.
  const handleFlowTypeChange = async (next) => {
    setFlowType(next);
    if (!currentFlow?.id) return;
    try {
      setCurrentFlow(await updateFlow(currentFlow.id, { flowType: next }));
    } catch (err) {
      showSnackbar(err.message || 'Could not change the flow type', 'error');
    }
  };
  useEffect(() => {
    if (currentFlow?.flowType && currentFlow.flowType !== flowType) setFlowType(currentFlow.flowType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFlow?.flowType]);

  // WhatsApp Preview must show exactly what Meta will render. We only enable it once the flow is
  // saved AND fully synced to Meta AND validates cleanly (locally + at Meta). Otherwise the preview
  // would be stale or broken, and because preview reads the flow back read-only, gating on SYNCED
  // guarantees the JSON on Meta already matches the editor (no re-upload, so a published flow is
  // never reverted to draft). "Still fetching the verdict" (PENDING/SYNCING) counts as not-ready.
  const liveSyncStatus = syncStatus?.syncStatus ?? currentFlow?.syncStatus;
  const liveValidationStatus = syncStatus?.validationStatus ?? currentFlow?.validationStatus;
  const previewBlockReason = !currentFlow?.id
    ? 'Save the flow to preview it'
    : !currentFlow?.whatsappFlowId
      ? 'Waiting for the flow to sync to WhatsApp…'
      : liveSyncStatus !== 'SYNCED'
        ? 'Waiting for validation to finish…'
        : (validationMap.totals.errorCount > 0 || liveValidationStatus === 'INVALID')
          ? 'Fix validation errors before previewing'
          : liveValidationStatus !== 'VALID'
            ? 'Waiting for validation to finish…'
            : null;
  const canPreview = !previewBlockReason;

  // When a flow finishes syncing INVALID, tell the backend whether our own builder also flagged it
  // (so the discrepancy corpus can surface the true misses). Report once per saved version.
  const reportedVerdictRef = useRef(null);
  useEffect(() => {
    if (!currentFlow?.id) return;
    if (syncStatus?.syncStatus === 'SYNCED' && syncStatus?.validationStatus === 'INVALID') {
      const key = `${currentFlow.id}:${currentFlow.updatedAt}`;
      if (reportedVerdictRef.current !== key) {
        reportedVerdictRef.current = key;
        reportValidationFeedback(currentFlow.id, validationMap.totals.errorCount);
        track('flow_validation_failed_at_meta', {
          flowId: currentFlow.id,
          builderFlaggedCount: validationMap.totals.errorCount,
          builderMissedIt: validationMap.totals.errorCount === 0,
        });
      }
    }
  }, [syncStatus, currentFlow?.id, currentFlow?.updatedAt, validationMap.totals.errorCount, reportValidationFeedback]);

  // The preview reads the flow back from Meta read-only by its real id (no JSON is sent), so we just
  // open the dialog. canPreview already guarantees the flow is saved + synced + valid.
  const handleOpenPreview = () => {
    track('preview_opened', { flowId: currentFlow?.id });
    setPreviewOpen(true);
  };

  // Reconcile a detected WhatsApp drift. Both directions route through the normal updateFlow path:
  // adopting remote overwrites the local JSON (a no-op re-sync since it now equals Meta); keeping
  // local re-saves our JSON so the sync worker pushes it back up, overwriting the WhatsApp edit.
  const handleAdoptRemote = async () => {
    if (!flowDrift || !currentFlow?.id) return;
    setReconciling(true);
    try {
      await updateFlow(currentFlow.id, { flowJson: flowDrift.remoteJson });
      resolveDrift();
      showSnackbar("Loaded WhatsApp's version of this flow", 'success');
    } catch (e) {
      showSnackbar("Could not load WhatsApp's version", 'error');
    } finally {
      setReconciling(false);
    }
  };

  const handleKeepLocal = async () => {
    if (!currentFlow?.id) return;
    setReconciling(true);
    try {
      const flowJson = await generateFlowJson();
      await updateFlow(currentFlow.id, { flowJson });
      resolveDrift();
      showSnackbar('Your version will be pushed to WhatsApp', 'success');
    } catch (e) {
      showSnackbar('Could not push your version to WhatsApp', 'error');
    } finally {
      setReconciling(false);
    }
  };

  // Deploy handler removed - now integrated into save

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box className="flow-builder" data-testid="flow-builder">
        {/* No-WABA warning: flows can be edited but not saved until the active
            environment has a WhatsApp Business Account connected. */}
        {!isConnected && (
          <Alert
            severity="warning"
            data-testid="no-waba-warning"
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => setConnectWabaOpen(true)}
                data-testid="connect-waba-from-builder"
              >
                Connect WhatsApp account
              </Button>
            }
            sx={{ borderRadius: 0 }}
          >
            To save this flow, connect a WhatsApp Business Account to this environment first.
          </Alert>
        )}
        {/* Header */}
        <Paper className="flow-builder__header" elevation={1}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, minWidth: 0, mr: 2 }}>
            {currentFlow && (
              <Typography variant="subtitle1" component="h1" noWrap sx={{ fontWeight: 600 }}>
                {currentFlow.name}
              </Typography>
            )}
            {currentFlow?.endpointUri && <DynamicFlowChip />}
          </Box>
          <Box className="flow-builder__actions">
            {/* Edit tools */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Tooltip title="Undo (⌘Z)">
                <span>
                  <IconButton size="small" onClick={undo} disabled={!canUndo} aria-label="Undo" data-testid="undo-button">
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Redo (⇧⌘Z)">
                <span>
                  <IconButton size="small" onClick={redo} disabled={!canRedo} aria-label="Redo" data-testid="redo-button">
                    <RedoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={currentFlow?.id ? 'Version history' : 'Save the flow to start tracking versions'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setHistoryOpen(true)}
                    disabled={!currentFlow?.id}
                    aria-label="Version history"
                    data-testid="open-version-history"
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

            <Box sx={{ mr: 1 }}>
              <FlowTypeControl value={flowType} onChange={handleFlowTypeChange} />
            </Box>
            {/* One validation control, the same one the assistant shows: builder checks plus
                WhatsApp's verdict, opening the same panel. */}
            <ValidationStatus
              validationMap={validationMap}
              screens={screens}
              meta={syncStatus}
              onOpen={() => setValidationPanelOpen(true)}
              onFix={onFixWithAssistant ? (problems) => onFixWithAssistant(problems) : undefined}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

            {/* Primary action + save/sync status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip
                title={
                  !isConnected
                    ? 'Connect a WhatsApp Business Account to save flows'
                    : (driftResolved ? '' : 'Checking for changes on WhatsApp…')
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    onClick={() => setSaveDialogOpen(true)}
                    disabled={!driftResolved || !isConnected}
                  >
                    {currentFlow?.id ? 'Update Flow' : 'Save Flow'}
                  </Button>
                </span>
              </Tooltip>
              <SaveStatusControl
                dirty={dirty}
                autoSaveError={autoSaveError}
                syncStatus={syncStatus}
                autoSaveEnabled={autoSaveEnabled}
                onToggleAutoSave={setAutoSaveEnabled}
                onSaveNow={handleSaveNow}
                canSave={driftResolved && isConnected}
              />
            </Box>

            <Tooltip title="More actions: send test to WhatsApp, auto-send triggers, import/export JSON, preview…">
              <IconButton
                size="small"
                onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                data-testid="flow-builder-more-menu"
                aria-label="More actions"
                sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1 }}
              >
                <MoreIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={moreAnchorEl} open={moreMenuOpen} onClose={closeMoreMenu}>
              <Tooltip title={previewBlockReason || ''} placement="left">
                <span>
                  <MenuItem
                    onClick={() => { closeMoreMenu(); handleOpenPreview(); }}
                    disabled={!canPreview}
                  >
                    <ListItemIcon><PhonePreviewIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>WhatsApp Preview</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              {/* Same gating as preview: the flow on Meta must already equal the editor, so
                  what arrives on the phone is exactly what's on screen. The person messages the
                  flow's number with a code, so this works for a demo and an account alike. */}
              <Tooltip title={previewBlockReason || ''} placement="left">
                <span>
                  <MenuItem
                    onClick={() => { closeMoreMenu(); setSendToPhoneOpen(true); }}
                    disabled={!canPreview}
                    data-testid="open-send-to-phone"
                  >
                    <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Try it on your phone</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              {/* Trigger config only needs the flow to exist server-side (the webhook
                  resolves it by id) - unlike preview/test-send it doesn't need a clean sync. */}
              <Tooltip title={currentFlow?.id ? '' : 'Save the flow first'} placement="left">
                <span>
                  <MenuItem
                    onClick={() => { closeMoreMenu(); setTriggerOpen(true); }}
                    disabled={!currentFlow?.id}
                    data-testid="open-connections"
                  >
                    <ListItemIcon><HubIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Connections</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>
          </Box>
        </Paper>

        {/* Main Content */}
        <Box className="flow-builder__content">
          {/* Component Palette */}
          <Paper className="flow-builder__sidebar" elevation={1}>
            <ComponentPalette />
          </Paper>

          {/* Canvas */}
          <Box className="flow-builder__canvas">
            <Paper className="canvas-header" elevation={1}>
              <DndContext
                sensors={screenTabSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleScreenDragEnd}
              >
                <SortableContext
                  items={screens.map(s => s.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  <Box className="screen-tabs">
                    {screens.map(screen => (
                      <EditableScreenTab
                        key={screen.id}
                        screen={screen}
                        isActive={activeScreen === screen.id}
                        canDelete={screens.length > 1}
                        onSelect={setActiveScreen}
                        onRename={renameScreen}
                        onDelete={removeScreen}
                        hasRouting={screen.routing?.rules?.length > 0}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
              {/* Screen actions stay pinned to the right; tabs wrap onto new lines
                  beside them rather than scrolling/hiding the Add button. */}
              <Box className="screen-actions">
                <Tooltip title="Duplicate the current screen">
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<DuplicateIcon fontSize="small" />}
                    onClick={() => duplicateScreen(activeScreen)}
                  >
                    Duplicate
                  </Button>
                </Tooltip>
                <Button variant="outlined" size="small" onClick={addScreen}>
                  + Add Screen
                </Button>
              </Box>
            </Paper>

            <ErrorBoundary label="The canvas hit an error">
              <Canvas
                screen={currentScreen}
                selectedComponent={selectedComponent}
                hoveredComponent={hoveredComponent}
                onSelectComponent={setSelectedComponent}
                onDeleteComponent={deleteComponent}
                onDuplicateComponent={duplicateComponent}
                addComponent={addComponent}
                onDropError={(message) => showSnackbar(message, 'error')}
                addComponentToBranch={addComponentToBranch}
                reorderBranchComponents={reorderBranchComponents}
                removeComponentFromBranch={removeComponentFromBranch}
                validationMap={validationMap}
              />
            </ErrorBoundary>
          </Box>

          {/* Properties Panel */}
          <Paper className="flow-builder__sidebar" elevation={1}>
            <ErrorBoundary label="This component's settings hit an error">
              <PropertiesPanel
                selectedComponent={selectedComponent}
                onUpdateComponent={updateComponentProperties}
                currentScreen={currentScreen}
                screens={screens}
                flowType={flowType}
                onRenameScreen={renameScreen}
                onUpdateRouting={updateScreenRouting}
              />
            </ErrorBoundary>
          </Paper>

          {/* Real-time Preview Panel, always visible (the show/hide toggle was removed as clutter) */}
          <Paper className="flow-builder__sidebar preview-panel" elevation={1}>
            <RealtimeFlowPreview
              screens={screens}
              activeScreen={activeScreen}
              flowName={currentFlow?.name}
            />
          </Paper>

        </Box>

        <DragOverlay>
          {activeId ? (
            <Box className="drag-overlay">
              Dragging component...
            </Box>
          ) : null}
        </DragOverlay>
      </Box>

      {/* Connect WABA (launched from the no-WABA warning banner) */}
      <ConnectWabaDialog
        open={connectWabaOpen}
        onClose={() => setConnectWabaOpen(false)}
        environment={selectedEnvironment}
      />

      {/* Save Flow Dialog */}
      <SaveFlowDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveFlow}
        onTestEndpoint={testEndpoint}
        flowSummary={flowSummary}
        existingFlow={currentFlow}
        flowType={flowType}
      />

      {/* Version History Drawer */}
      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        flowId={currentFlow?.id}
        onRestored={(restored, versionNumber) => {
          setReloadNonce((n) => n + 1);
          showSnackbar(`Restored to version ${versionNumber}`, 'success');
        }}
      />

      {/* Meta's own rendering of the flow: the same dialog the assistant opens */}
      <WhatsAppPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} flowId={currentFlow?.id} />

      {/* The flow on the builder's own phone, fetched with a code they message us */}
      <SendToPhoneDialog
        open={sendToPhoneOpen}
        onClose={() => setSendToPhoneOpen(false)}
        flow={currentFlow}
      />

      {/* Everything this flow is connected to: keywords, live data, where answers go */}
      <FlowConnectionsDrawer
        open={triggerOpen}
        onClose={() => { setTriggerOpen(false); refreshLiveData(); }}
        flow={currentFlow}
      />

      {/* WhatsApp drift reconciliation, user must choose a direction (no dismiss). */}
      <Dialog open={!!flowDrift} maxWidth="sm" fullWidth>
        <DialogTitle>This flow changed on WhatsApp</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The version stored on WhatsApp differs from your local copy in FlowGate: it was likely
            edited directly in WhatsApp Manager. Choose which version to keep.
          </Alert>
          <Typography variant="body2" color="text.secondary" component="div">
            <strong>Use WhatsApp&apos;s version</strong>, replace your local copy with what&apos;s on
            WhatsApp. Builder-only annotations (conditions, routing labels) may need to be re-applied.
            <br /><br />
            <strong>Keep my FlowGate version</strong>, overwrite the flow on WhatsApp with your
            current version.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleAdoptRemote} disabled={reconciling}>
            Use WhatsApp&apos;s version
          </Button>
          <Button onClick={handleKeepLocal} variant="contained" disableElevation disabled={reconciling}>
            Keep my FlowGate version
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Summary Panel */}
      <ValidationSummaryPanel
        open={validationPanelOpen}
        onClose={() => setValidationPanelOpen(false)}
        validationMap={validationMap}
        screens={screens}
        meta={syncStatus}
        onJumpToComponent={handleJumpToComponent}
        onJumpToScreen={handleJumpToScreen}
        onFixWithAssistant={onFixWithAssistant
          ? () => { setValidationPanelOpen(false); onFixWithAssistant(problemsFromValidationMap(validationMap, screens, syncStatus)); }
          : undefined}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={hideSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </DndContext>
  );
};

export default FlowBuilder;